import React, { useEffect, useState } from 'react';
import { categoriesAPI, businessesAPI } from '../services/api';
import { formatApiError } from '../utils/errorMessage';
import { withArticle } from '../utils/article';
import { readSavedBusiness } from '../utils/businessSelection';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  SCOPE_BUSINESS, SCOPE_ALL, SCOPE_HELP, isBuiltIn, scopeOf, scopeLabel, defaultBusinessId, categoryPayload
} from '../features/categories/categoryScope';

// Shown when a category with the same name and type already exists.
export function duplicateCategoryMessage(form) {
  const name = String(form?.name || '').trim();
  return `You already have ${withArticle(form?.type || '')} category named "${name}". Use that one instead, or pick a different name.`;
}

// Built-in categories every business can use.
export const isShared = isBuiltIn;

// The form's scope help (both choices), kept as one export for older imports.
export const CATEGORY_SCOPE_HELP = SCOPE_HELP[SCOPE_BUSINESS];

const DOT = ' \u2022 ';
const blankForm = (type, businessId) => ({ name: '', type: type || 'expense', color: '', icon: '', scope: SCOPE_BUSINESS, business_id: businessId });

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // '' (all types) | 'expense' | 'income' | 'equity' | 'transfer'
  const [form, setForm] = useState(blankForm('expense', null));
  const [editing, setEditing] = useState(null);
  const selected = readSavedBusiness();

  const load = async () => {
    try {
      setLoading(true);
      const params = typeFilter ? { type: typeFilter } : {};
      const res = await categoriesAPI.getAll(params);
      setCategories(res.data || []);
      setError('');
    } catch (e) {
      setError(formatApiError(e, 'Failed to load categories'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [typeFilter]);
  useEffect(() => {
    let live = true;
    Promise.resolve(businessesAPI && businessesAPI.getAll ? businessesAPI.getAll() : { data: [] })
      .then((res) => {
        if (!live) return;
        const list = Array.isArray(res && res.data) ? res.data : [];
        setBusinesses(list);
        setForm((f) => (f.business_id ? f : { ...f, business_id: defaultBusinessId(readSavedBusiness(), list) }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const resetForm = () => setForm(blankForm(typeFilter, defaultBusinessId(selected, businesses)));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!form.name) return setError('Name is required');
      if (!form.business_id) return setError('Add a business on the Dashboard first.');
      if (editing) {
        await categoriesAPI.update(editing.id, categoryPayload(form));
        setEditing(null);
      } else {
        const created = await categoriesAPI.create(categoryPayload(form));
        if (created?.data?.existing) {
          return setError(duplicateCategoryMessage(form));
        }
      }
      resetForm();
      load();
    } catch (e) {
      setError(formatApiError(e, 'Save failed'));
    }
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '', type: c.type || 'expense', color: c.color || '', icon: c.icon || '',
      scope: scopeOf(c), business_id: c.business_id ? Number(c.business_id) : defaultBusinessId(selected, businesses)
    });
  };

  const remove = async (c) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      const businessId = c.business_id || defaultBusinessId(selected, businesses);
      await categoriesAPI.delete(c.id, businessId ? { business_id: businessId } : undefined);
      if (editing && editing.id === c.id) setEditing(null);
      load();
    } catch (e) {
      setError(formatApiError(e, 'Delete failed'));
    }
  };

  // An all-businesses category cannot be narrowed later, and a business-only
  // one keeps its business; only businesses the user can open are listed.
  const narrowLocked = Boolean(editing && !editing.business_id);
  const businessLocked = Boolean(editing && editing.business_id);

  return (
    <ErrorBoundary section="Categories">
      <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Categories</h2>
          <p className="text-gray-600">Manage income, expense, equity and transfer categories used across budgets and transactions.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Filter:</label>
          <select value={typeFilter} onChange={(e)=>setTypeFilter(e.target.value)} className="border rounded px-3 py-2">
            <option value="">All</option>
            <option value="expense">Expense</option>
            <option value="income">Income</option>
            <option value="equity">Equity</option>
            <option value="transfer">Transfer</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded" role="alert">
          {formatApiError(error, 'An error occurred')}
        </div>
      )}

      <div className="bg-white rounded shadow p-6">
        <h3 className="text-lg font-semibold mb-4">{editing ? 'Edit Category' : 'Add Category'}</h3>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input type="text" required placeholder="Name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="border rounded px-3 py-2" />
          <select value={form.type} onChange={e=>setForm({...form, type:e.target.value})} className="border rounded px-3 py-2">
            <option value="expense">Expense</option>
            <option value="income">Income</option>
            <option value="equity">Equity (owner contribution or draw, not in P&amp;L)</option>
            <option value="transfer">Transfer (between accounts, asset sale, loan principal; not in P&amp;L)</option>
          </select>
          <input type="text" placeholder="#Color (optional)" value={form.color} onChange={e=>setForm({...form, color:e.target.value})} className="border rounded px-3 py-2" />
          <input type="text" placeholder="Icon (optional)" value={form.icon} onChange={e=>setForm({...form, icon:e.target.value})} className="border rounded px-3 py-2" />
          <label className="flex flex-col text-sm text-gray-700">
            Available in
            <select aria-label="Available in" value={form.scope} onChange={e=>setForm({...form, scope:e.target.value})} className="border rounded px-3 py-2 mt-1">
              <option value={SCOPE_BUSINESS} disabled={narrowLocked}>This business only</option>
              <option value={SCOPE_ALL}>All my businesses</option>
            </select>
          </label>
          {form.scope === SCOPE_BUSINESS && businesses.length > 0 && (
            <label className="flex flex-col text-sm text-gray-700">
              Business
              <select aria-label="Business" value={form.business_id || ''} disabled={businessLocked} onChange={e=>setForm({...form, business_id:Number(e.target.value)})} className="border rounded px-3 py-2 mt-1">
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name || b.business_name || `Business ${b.id}`}</option>)}
              </select>
            </label>
          )}
          <p className="md:col-span-4 text-sm text-gray-500">{SCOPE_HELP[form.scope] || SCOPE_HELP[SCOPE_BUSINESS]}</p>
          <div className="md:col-span-4 flex justify-end gap-2">
            {editing && (
              <button type="button" onClick={()=>{ setEditing(null); resetForm(); }} className="px-4 py-2 border rounded">Cancel</button>
            )}
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">{editing ? 'Update' : 'Add'}</button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">{typeFilter ? `${typeFilter[0].toUpperCase()+typeFilter.slice(1)} ` : ''}Categories</h3>
        </div>
        {loading ? (
          <div className="p-6">Loading...</div>
        ) : (
          <div className="divide-y">
            {categories.length === 0 ? (
              <div className="p-6 text-gray-500">No categories found.</div>
            ) : categories.map(c => (
              <div key={c.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.type}{DOT}{scopeLabel(c, businesses)}{c.color ? `${DOT}${c.color}` : ''}{c.icon ? `${DOT}${c.icon}` : ''}</div>
                </div>
                {!isBuiltIn(c) && (
                  <div className="flex items-center gap-2">
                    <button onClick={()=>startEdit(c)} className="px-3 py-1 border rounded">Edit</button>
                    <button onClick={()=>remove(c)} className="px-3 py-1 border rounded text-red-700">Delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </ErrorBoundary>
  );
};

export default Categories;